import { Modal, Popover, Tabs, TabsProps } from 'antd';
import React from 'react';
import classes from '../RelatedRooms.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import cn from 'classnames';
import NDAAgreement from 'components/NDAElements/NDAAgreement';
import fetchDocumentToViewer from 'store/reducers/viewerCreator';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { IDocument } from 'interfaces';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import dayjs from 'dayjs';
import api from 'api';
import StateOfTerm from 'components/NDAElements/StateOfTerm';
import { setNewRoomIds, setRooms } from 'store/slices/userDataSlice';
import { resetViewerSlice } from 'store/slices/viewerSlice';
import { getTheme } from 'helpers/getTheme';
import { changeStatusTerm, findParentTerm } from '../helpers/updateTreeData';
import { DataNode } from 'antd/es/tree';
import RelatedRoom from './RelatedRoom';
import Assets from 'assets/icons';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import RowLoader from 'components/Sceletons/RowLoader';

type ModalTermType = {
  open: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTerm: IDocument;
  setSelectedTerm: React.Dispatch<React.SetStateAction<IDocument | null>>;
  setRelatedRoomsTerms: React.Dispatch<React.SetStateAction<IDocument[]>>;
  setTreeData: React.Dispatch<React.SetStateAction<DataNode[]>>;
  setExpandedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  setLoadedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  relatedRoomsTerms: IDocument[];
};

const ModalRoomTerm = ({
  open, setIsOpen,
  selectedTerm,
  setSelectedTerm,
  setRelatedRoomsTerms,
  setTreeData,
  setExpandedKeys,
  setLoadedKeys,
  relatedRoomsTerms,
}: ModalTermType) => {
  const [isCheckingGroups, setisCheckingGroups] = React.useState(false);
  const [isThisAccessPointForMyGroup, setisThisAccessPointForMyGroup] = React.useState(false);
  const [activeTabKey, setActiveTabKey] = React.useState('agreement');

  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userData, userPermissions, available_rooms, newRoomIds } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const canViewState = userPermissions.can_create_access_point && selectedTerm.user_status?.status_access === 'approved';

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  
  const formatNDA = selectedTerm.name?.split('.')?.reverse()[0];  
  const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';

  const checkIsItAccessPointForMyGroup = async (payload: {room_id: string, current_user: string; by_current_user: boolean}) => {
    setisCheckingGroups(true);
    try {      
      const response = await api.getMyGroupInRoom(payload);
      const myGroupIdIn = response.data.group_id;
      const arrOfGroupsIds = selectedTerm.groups?.map(group => group.id);
      
      if (arrOfGroupsIds?.includes(myGroupIdIn)) {
        setisThisAccessPointForMyGroup(true);
      };
    } catch {} finally {
      setisCheckingGroups(false);
    }
  };
  
  React.useEffect(() => {
    if (dataRoom && selectedTerm) {
      const parentTerm = findParentTerm(relatedRoomsTerms, selectedTerm.id);
      const parentTermsAproved = parentTerm ? parentTerm?.user_status?.status_access === 'approved' : true;      
      if (selectedTerm.for_all_groups && parentTermsAproved) {        
        setisThisAccessPointForMyGroup(true);
      } else {
        parentTermsAproved && checkIsItAccessPointForMyGroup({room_id: selectedTerm?.room?.id!, current_user: userData?.id!, by_current_user: true});
      };
    };
  }, [dataRoom?.id, selectedTerm]);

  React.useEffect(() => {
    if (dataRoom) {
      const fileInfo = selectedTerm?.name!.split(/\.\b/).reverse();
      dispatch(
        fetchDocumentToViewer({
          token: selectedTerm.token,
          id: dataRoom?.id!,
          entity: selectedTerm.id,
          extension: selectedTerm?.extension || fileInfo[0],
          is_agreement: true,
          locale: i18n.language,
          action: 'view',
        })
      );
    };

    return () => {
      dispatch(resetViewerSlice());
    }
  }, [dataRoom?.id]);

  const changeTermStatus = async (status: number) => {
    try {
      const UTCDate = dayjs().valueOf() + new Date().getTimezoneOffset() * 60 * 1000;

      const changeStatusTree = (arrTerms: DataNode[], key: string, updatedSelectedTerm: IDocument): DataNode[] => {        
        return arrTerms.map(item => {
          if (String(item.key).split('_')[1] === key) {
            
            return {
              ...item,
              isLeaf: false,
              children: undefined,
              title: <RelatedRoom
                key={selectedTerm.id}
                document={updatedSelectedTerm}
                setIsOpen={setIsOpen}
                setSelectedTerm={setSelectedTerm}
                setRelatedRoomsTerms={setRelatedRoomsTerms}
                setTreeData={setTreeData}
              />,
            }
          } else {
            if (item.children) {
              return {
                ...item,
                children: changeStatusTree(item.children, key, updatedSelectedTerm)
              }
            } else {
              return item;
            }
          }
        });
      };

      if (status === 1) {
        await api.acceptRoomTerm({access_point_id: selectedTerm.id});
        const updatedSelectedTerm = {
          ...selectedTerm,
          user_status: {
            ...selectedTerm.user_status!,
            accepted_at: UTCDate,
            status_access: 'approved'
          }
        };

        setLoadedKeys(prev => prev.filter(el => selectedTerm.id !== String(el).split('_')[1]));
        setExpandedKeys(prev => prev.filter(el => selectedTerm.id !== String(el).split('_')[1]))
        setRelatedRoomsTerms(prev => changeStatusTerm(prev, selectedTerm.id, 'approved'));
        setTreeData(prev => changeStatusTree(prev, selectedTerm.id, updatedSelectedTerm));
        setSelectedTerm(updatedSelectedTerm);

        if (selectedTerm?.related_room) {
          try {
            const relatedRoom = await api.getRoom(selectedTerm?.related_room?.id!);        
            const withNewRoom = [
              { 
                ...relatedRoom.data,
                created_at: Date.parse(String(relatedRoom.data.created_at)),
                last_action_timestamp: Date.parse(String(relatedRoom.data.last_action_timestamp || relatedRoom.data.created_at)),
              }, 
              ...available_rooms.filter(room => room.id !== relatedRoom.data.id)
            ].sort((a,b) => b.last_action_timestamp - a.last_action_timestamp);
    
            dispatch(setNewRoomIds([...newRoomIds, relatedRoom.data.id]));
            dispatch(setRooms(withNewRoom));
          } catch {}
        }

      } else {
        await api.rejectRoomTerm({access_point_id: selectedTerm.id});
        const updatedSelectedTerm = {
          ...selectedTerm,
          user_status: {
            ...selectedTerm.user_status!,
            accepted_at: UTCDate,
            status_access: 'denied'
          }
        }
        setRelatedRoomsTerms(prev => changeStatusTerm(prev, selectedTerm.id, 'denied'));
        setTreeData(prev => changeStatusTree(prev, selectedTerm.id, updatedSelectedTerm));
        setSelectedTerm(updatedSelectedTerm);
      }
      status === 1 
        ? messageApi?.success(t('Settings.terms.acceptedTerm'))
        : messageApi?.warning(t('Settings.terms.rejectedTerm'))
    } catch (e) {
      console.log(e);
      status === 1
        ? messageApi?.error(t('Settings.terms.acceptedError'))
        : messageApi?.error(t('Settings.terms.rejectedError'))
    }
  };
  const TabsData = React.useMemo(() => {
    const tabs: TabsProps['items'] = [
      {
        key: 'agreement',
        label: t('Confirm.agreement'),
        children: <NDAAgreement
          userAcceptNDA={selectedTerm?.user_status?.status_access === 'approved' || selectedTerm?.user_status?.status_access === 'accepted'}
          changeTermStatus={changeTermStatus}
          isThisAccessPointForMyGroup={isThisAccessPointForMyGroup}
        />,
      },
    ];    

    if (canViewState) {
      canViewState && tabs.push({
        key: 'state',
        label: t('Settings.terms.state.title'),
        children: <StateOfTerm
          stateRequest={() => api.getStateOfRoomTerm(selectedTerm?.id!, dataRoom?.id!)}
          title={t('Settings.terms.titleTermsModal')}
          documentName={selectedTerm.name}
          documentId={selectedTerm.id}
          entity={'access_point'}
          groupsOfTerm={selectedTerm?.for_all_groups ? [{id: 'all', name: 'all'}] : selectedTerm.groups}
        />,
      });
    }

    return tabs
  }, [canViewState, selectedTerm, isThisAccessPointForMyGroup]);

  const titleFile = (
    <div>
      <span>{t('Settings.relatedRooms.titleTermsRoomModal')}</span> 
      <span>{selectedTerm?.related_room?.name}</span>
    </div>
  );

  const popoverContent = React.useCallback((term: IDocument) => {
    if (term.for_all_groups) {
      return t('Settings.relatedRooms.allRolesAvailable');
    } else {
      const rolesNames = term.groups?.map(group => defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name)
      return <span>{t('Settings.relatedRooms.forRolesAvailable')} {rolesNames?.join(', ')}</span>;
    }
  }, [selectedTerm]);

  const getStatusIcon = (status?: string, notMy?: boolean) => {
    if (notMy) return (
      <Popover
        style={{maxWidth: 600}}
        content={popoverContent(selectedTerm)}
      >
        <span><Assets.MembersIcon className={classes.membersIcon} /></span>
      </Popover>
    );
    
    switch(status) {
      case 'denied':
        return <CloseOutlined className={classes.rejectIcon}/>
      case 'approved':
        return <CheckOutlined className={classes.acceptIcon}/>
      case 'accepted':
          return <CheckOutlined className={classes.acceptIcon}/>
      default: 
        return <ClockCircleOutlined className={classes.notAcceptIcon}/>
    };
  };

  const getClassOfWrap = (status?: string, notMy?: boolean) => {
    switch(status) {
      case 'denied':
        return classes.reject
      case 'approved':
        return classes.accept
      case 'accepted':
        return classes.accept
      default: 
        return classes.nonAccept
    };
  };

  const getStatusText = (status?: string, notMy?: boolean) => {
    if (notMy) return '';

    switch(status) {
      case 'denied':
        return t('Settings.terms.state.rejected')
      case 'approved':
        return <>
          <div>{t('Confirm.signedByMe')}</div>
          <div className={classes.date}>{t('Confirm.date')}</div>
          <div className={classes.dateValue}>{getDateWithTimeZone(selectedTerm?.user_status?.accepted_at!, userData?.date_format!)}</div>
        </>
      case 'accepted': 
        return <div>{t('Confirm.signedByMe')}</div>
      default: 
        return t('Settings.terms.state.nonAccepted')
    };
  };

  return (
    <Modal
      centered
      width={'90%'}
      onCancel={() => setIsOpen(false)}
      open={open}
      className={cn(classes.modalRoomTermWrap, getTheme())}
      title={titleFile} 
      footer={null}
    >
      <>
        <div className={classes.titleWrap}>
          <div className={classes.statusWrap}>
            <div className={classes.docName}>
              <div style={{ fontSize: 20 }}>{documnentIcons[icon as keyof typeof documnentIcons]}</div>
              <div className={classes.currentName}>{selectedTerm.name}</div>
            </div>
            
            {isCheckingGroups
              ? <RowLoader height={22} width={22} padding={'0 0 0 6px'} />
              : getStatusIcon(String(selectedTerm?.user_status?.status_access), !isThisAccessPointForMyGroup)
            }
          </div>
          
          {<div className={cn(classes.dateWrap, getClassOfWrap(String(selectedTerm?.user_status?.status_access)))}>
            {getStatusText(String(selectedTerm?.user_status?.status_access), !isThisAccessPointForMyGroup)}
          </div>}
        </div>
    
        <Tabs
          className={classes.tabs}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          items={TabsData}
          type='card'
        />
      </>
    </Modal>
  );
};

export default ModalRoomTerm;
